import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { rejectBody } from '../../utils/apiError';

export const fetchUsers = createAsyncThunk('users/fetchAll', async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get('/api/super-admin/users');
        return response.data;
    } catch (error) {
        return rejectWithValue(rejectBody(error));
    }
});

export const fetchRoles = createAsyncThunk('users/fetchRoles', async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get('/api/super-admin/roles');
        return response.data;
    } catch (error) {
        return rejectWithValue(rejectBody(error));
    }
});

export const createUser = createAsyncThunk('users/create', async (userData, { rejectWithValue }) => {
    try {
        const response = await axios.post('/api/super-admin/users', userData);
        return response.data;
    } catch (error) {
        return rejectWithValue(rejectBody(error));
    }
});

export const updateUser = createAsyncThunk('users/update', async ({ id, data }, { rejectWithValue }) => {
    try {
        const response = await axios.put(`/api/super-admin/users/${id}`, data);
        return response.data;
    } catch (error) {
        return rejectWithValue(rejectBody(error));
    }
});

export const deleteUser = createAsyncThunk('users/delete', async (id, { rejectWithValue }) => {
    try {
        await axios.delete(`/api/super-admin/users/${id}`);
        return id;
    } catch (error) {
        return rejectWithValue(rejectBody(error));
    }
});

const usersSlice = createSlice({
    name: 'users',
    initialState: {
        users: [],
        roles: [],
        loading: false,
        error: null,
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Users
            .addCase(fetchUsers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUsers.fulfilled, (state, action) => {
                state.loading = false;
                state.users = action.payload.data || action.payload;
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || action.payload?.message || action.error.message;
            })
            // Fetch Roles
            .addCase(fetchRoles.fulfilled, (state, action) => {
                state.roles = action.payload.data || action.payload;
            })
            // Create User
            .addCase(createUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createUser.fulfilled, (state, action) => {
                state.loading = false;
                const newUser = action.payload.data || action.payload;
                state.users.unshift(newUser);
            })
            .addCase(createUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || action.payload?.message || action.error.message;
            })
            // Update User
            .addCase(updateUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.loading = false;
                const updatedUser = action.payload.data || action.payload;
                const index = state.users.findIndex((u) => u.id === updatedUser.id);
                if (index !== -1) {
                    // MERGE, never replace: a response without the role/garage
                    // includes must not strip them off the row — filteredUsers
                    // reads those fields, so replacing dropped the freshly
                    // edited user out of the list (and out of any filter).
                    state.users[index] = { ...state.users[index], ...updatedUser };
                }
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || action.payload?.message || action.error.message;
            })
            // Delete User
            .addCase(deleteUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteUser.fulfilled, (state, action) => {
                state.loading = false;
                state.users = state.users.filter((u) => u.id !== action.payload);
            })
            .addCase(deleteUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || action.payload?.message || action.error.message;
            });
    },
});

export const { clearError } = usersSlice.actions;

export default usersSlice.reducer;
